import { useEffect } from 'react'
import {
  FormControl,
  FormLabel,
  Input,
  Switch,
  HStack,
  VStack,
  Text,
  Icon,
  Box,
  Tooltip,
  Collapse,
  useColorMode,
  IconButton,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react'
import { RefreshCw, Key, Link2, ShieldCheck, Globe, PenLine } from 'lucide-react'
import { generatePasscode } from '@/lib/crypto/passcode'
import { initializePasscodeOptions, enforcePasscodeTierRules } from '@/lib/utils/passcode-config'
import type { PasscodeOptions } from '@/types/share'

interface PasscodeConfigProps {
  options: PasscodeOptions
  onChange: (options: PasscodeOptions) => void
  tier?: 'free' | 'starter' | 'pro'
}

export function PasscodeConfig({ options, onChange, tier = 'free' }: PasscodeConfigProps) {
  const isPro = tier === 'pro'
  const { colorMode } = useColorMode()

  useEffect(() => {
    const initialized = initializePasscodeOptions(options)
    if (initialized) onChange(initialized)
  }, [])

  // Reset useCustomPasscode if user is not on Pro tier (custom passcodes are Pro only)
  useEffect(() => {
    const enforced = enforcePasscodeTierRules(options, tier)
    if (enforced) onChange(enforced)
  }, [isPro]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegenerate = () => {
    onChange({ ...options, passcode: generatePasscode() })
  }

  const handleCustomToggle = (useCustom: boolean) => {
    onChange({
      ...options,
      useCustomPasscode: useCustom,
      passcode: useCustom ? '' : generatePasscode(),
    })
  }

  const handlePublicToggle = (isPublic: boolean) => {
    if (isPublic) {
      // Switching to public - clear passcode settings
      onChange({
        ...options,
        isPublic: true,
        passcode: '',
        embedInUrl: false,
        useCustomPasscode: false,
      })
    } else {
      // Switching to private - generate passcode
      onChange({
        ...options,
        isPublic: false,
        passcode: generatePasscode(),
        embedInUrl: true,
      })
    }
  }

  return (
    <VStack spacing={4} align="stretch">
      <HStack justify="space-between">
        <HStack spacing={2} flex={1}>
          <Icon as={Globe} boxSize={4} color="stone.500" />
          <Box>
            <FormLabel mb={0}>Public share</FormLabel>
            <Text fontSize="xs" color="stone.500">
              Anyone with the link can view without a passcode
            </Text>
          </Box>
        </HStack>
        <Switch
          isChecked={options.isPublic}
          onChange={(e) => handlePublicToggle(e.target.checked)}
        />
      </HStack>

      <Collapse in={!options.isPublic} animateOpacity>
        <VStack spacing={4} align="stretch">
          <FormControl>
            <HStack spacing={3} align="center" justify="space-between">
              <HStack spacing={2}>
                <Icon as={Key} boxSize={4} color="stone.500" />
                <FormLabel mb={0}>Passcode</FormLabel>
              </HStack>
              <InputGroup size="sm" maxW="220px">
                <Input
                  type="text"
                  value={options.passcode}
                  onChange={(e) => onChange({ ...options, passcode: e.target.value })}
                  placeholder={options.useCustomPasscode ? 'Enter your passcode' : ''}
                  readOnly={!options.useCustomPasscode}
                  fontFamily={!options.useCustomPasscode ? 'mono' : undefined}
                  bg={!options.useCustomPasscode ? (colorMode === 'dark' ? 'stone.700' : 'stone.50') : undefined}
                  pr={!options.useCustomPasscode ? '10' : undefined}
                />
                {!options.useCustomPasscode && (
                  <InputRightElement>
                    <Tooltip label="Regenerate passcode" placement="top">
                      <IconButton
                        aria-label="Regenerate passcode"
                        icon={<RefreshCw size={14} />}
                        size="xs"
                        variant="ghost"
                        onClick={handleRegenerate}
                      />
                    </Tooltip>
                  </InputRightElement>
                )}
              </InputGroup>
            </HStack>
          </FormControl>

          <Tooltip
            label="Custom passcodes require Pro tier"
            isDisabled={isPro}
            hasArrow
            placement="top"
          >
            <HStack justify="space-between" opacity={!isPro ? 0.5 : 1}>
              <HStack spacing={2} flex={1}>
                <Icon as={PenLine} boxSize={4} color="stone.500" />
                <Box>
                  <FormLabel mb={0}>Use custom passcode {!isPro && '(Pro)'}</FormLabel>
                  <Text fontSize="xs" color="stone.500">
                    Enter your own passcode instead of auto-generated
                  </Text>
                </Box>
              </HStack>
              <Switch
                isChecked={options.useCustomPasscode}
                onChange={(e) => handleCustomToggle(e.target.checked)}
                isDisabled={!isPro}
              />
            </HStack>
          </Tooltip>

          <HStack justify="space-between">
            <HStack spacing={2} flex={1}>
              <Icon as={Link2} boxSize={4} color="stone.500" />
              <Box>
                <FormLabel mb={0}>Embed passcode in link</FormLabel>
                <Text fontSize="xs" color="stone.500">
                  One-click access without entering passcode
                </Text>
              </Box>
            </HStack>
            <HStack spacing={3}>
              <Tooltip
                label="The passcode never leaves your browser. Content is encrypted client-side before upload."
                hasArrow
                placement="top"
                bg="stone.700"
                color="white"
                fontSize="xs"
                px={3}
                py={2}
                borderRadius="md"
              >
                <HStack spacing={1} color="stone.500" cursor="help" fontSize="xs">
                  <Icon as={ShieldCheck} boxSize={4} color="green.500" />
                  <Text>End-to-end encrypted</Text>
                </HStack>
              </Tooltip>
              <Switch
                isChecked={options.embedInUrl}
                onChange={(e) =>
                  onChange({ ...options, embedInUrl: e.target.checked })
                }
              />
            </HStack>
          </HStack>
        </VStack>
      </Collapse>
    </VStack>
  )
}
